import { create } from 'zustand';
import { createId } from '../lib/id';
import { createEmptyWorkspace, createReelProject } from '../lib/defaults';
import { loadWorkspace, saveWorkspace, clearWorkspace, pruneMedia, estimateStorage } from '../lib/db';
import { releaseAllObjectUrls } from '../lib/media';
import type {
  CaptionSettings,
  ExportSettings,
  ImageClip,
  MediaAsset,
  MusicSettings,
  ReelProject,
  SharedDefaults,
  VoiceoverSettings,
  WatermarkSettings,
  Workspace,
} from '@/types';

interface WorkspaceState {
  workspace: Workspace;
  loaded: boolean;
  debugMode: boolean;
  storageWarning: string | null;

  initialize: () => Promise<void>;
  setDebugMode: (on: boolean) => void;

  createProject: (title?: string) => string;
  duplicateProject: (id: string) => string;
  deleteProject: (id: string) => void;
  renameProject: (id: string, title: string) => void;
  reorderProjects: (order: string[]) => void;
  setActiveProject: (id: string | null) => void;

  updateShared: <K extends keyof SharedDefaults>(key: K, patch: Partial<SharedDefaults[K]>) => void;

  addImages: (projectId: string, assets: MediaAsset[], defaultDuration: number) => void;
  reorderImages: (projectId: string, orderedIds: string[]) => void;
  removeImage: (projectId: string, imageId: string) => void;
  replaceImage: (projectId: string, imageId: string, mediaId: string) => void;
  updateImageClip: (projectId: string, imageId: string, patch: Partial<ImageClip>) => void;
  applyDurationToAll: (projectId: string, duration: number) => void;

  updateVoiceover: (projectId: string, patch: Partial<VoiceoverSettings>) => void;
  updateCaptions: (projectId: string, patch: Partial<CaptionSettings>) => void;
  updateMusic: (projectId: string, patch: Partial<MusicSettings>) => void;
  updateWatermark: (projectId: string, patch: Partial<WatermarkSettings>) => void;
  updateExport: (projectId: string, patch: Partial<ExportSettings>) => void;

  exportProjectJson: (id: string) => string;
  importProjectJson: (json: string) => string;

  enqueueJob: (projectId: string) => string;
  updateJob: (jobId: string, patch: Partial<import('@/types').RenderJob>) => void;
  removeJob: (jobId: string) => void;
  reorderJobs: (order: string[]) => void;

  clearAllData: () => Promise<void>;
  checkStorageHealth: () => Promise<void>;
}

let saveTimer: ReturnType<typeof setTimeout> | null = null;
function scheduleSave(workspace: Workspace) {
  if (saveTimer) clearTimeout(saveTimer);
  saveTimer = setTimeout(() => {
    void saveWorkspace(workspace);
  }, 400);
}

function touch(project: ReelProject): ReelProject {
  return { ...project, updatedAt: Date.now() };
}

export const useWorkspaceStore = create<WorkspaceState>((set, get) => ({
  workspace: createEmptyWorkspace(),
  loaded: false,
  debugMode: false,
  storageWarning: null,

  initialize: async () => {
    const loaded = await loadWorkspace();
    set({ workspace: loaded ?? createEmptyWorkspace(), loaded: true });
    await get().checkStorageHealth();
  },

  setDebugMode: (on) => set({ debugMode: on }),

  createProject: (title) => {
    const project = createReelProject(title ?? `Reel ${Object.keys(get().workspace.projects).length + 1}`);
    set((state) => {
      const workspace: Workspace = {
        ...state.workspace,
        projects: { ...state.workspace.projects, [project.id]: project },
        projectOrder: [...state.workspace.projectOrder, project.id],
        activeProjectId: project.id,
        updatedAt: Date.now(),
      };
      scheduleSave(workspace);
      return { workspace };
    });
    return project.id;
  },

  duplicateProject: (id) => {
    const source = get().workspace.projects[id];
    if (!source) return id;
    const now = Date.now();
    const copy: ReelProject = {
      ...structuredClone(source),
      id: createId('reel'),
      title: `${source.title} (copy)`,
      createdAt: now,
      updatedAt: now,
    };
    set((state) => {
      const order = [...state.workspace.projectOrder];
      const idx = order.indexOf(id);
      order.splice(idx + 1, 0, copy.id);
      const workspace: Workspace = {
        ...state.workspace,
        projects: { ...state.workspace.projects, [copy.id]: copy },
        projectOrder: order,
        activeProjectId: copy.id,
        updatedAt: now,
      };
      scheduleSave(workspace);
      return { workspace };
    });
    return copy.id;
  },

  deleteProject: (id) => {
    set((state) => {
      const projects = { ...state.workspace.projects };
      delete projects[id];
      const jobs = { ...state.workspace.jobs };
      for (const jobId of Object.keys(jobs)) {
        if (jobs[jobId].projectId === id) delete jobs[jobId];
      }
      const projectOrder = state.workspace.projectOrder.filter((pid) => pid !== id);
      const activeProjectId = state.workspace.activeProjectId === id ? projectOrder[0] ?? null : state.workspace.activeProjectId;
      const workspace: Workspace = { ...state.workspace, projects, jobs, projectOrder, activeProjectId, updatedAt: Date.now() };
      scheduleSave(workspace);
      return { workspace };
    });
  },

  renameProject: (id, title) => {
    updateProjectField(set, id, (p) => touch({ ...p, title }));
  },

  reorderProjects: (order) => {
    set((state) => {
      const workspace = { ...state.workspace, projectOrder: order, updatedAt: Date.now() };
      scheduleSave(workspace);
      return { workspace };
    });
  },

  setActiveProject: (id) => {
    set((state) => ({ workspace: { ...state.workspace, activeProjectId: id } }));
  },

  updateShared: (key, patch) => {
    set((state) => {
      const shared = { ...state.workspace.shared, [key]: { ...state.workspace.shared[key], ...patch } };
      const workspace = { ...state.workspace, shared, updatedAt: Date.now() };
      scheduleSave(workspace);
      return { workspace };
    });
  },

  addImages: (projectId, assets, defaultDuration) => {
    updateProjectField(set, projectId, (p) => {
      const startOrder = p.images.length;
      const newClips: ImageClip[] = assets.map((asset, i) => ({
        id: createId('img'),
        type: 'image',
        mediaId: asset.id,
        duration: defaultDuration,
        order: startOrder + i,
        fit: 'cover',
        backgroundColor: '#000000',
        transitionIn: 'fade',
        transitionDuration: 0.5,
        kenBurns: {
          enabled: true,
          endScale: 1.12,
          startFocus: { x: 0.5, y: 0.5 },
          endFocus: { x: 0.5, y: 0.5 },
        },
      }));
      return touch({ ...p, images: [...p.images, ...newClips] });
    });
  },

  reorderImages: (projectId, orderedIds) => {
    updateProjectField(set, projectId, (p) => {
      const byId = new Map(p.images.map((img) => [img.id, img]));
      const images = orderedIds
        .map((id, index) => {
          const img = byId.get(id);
          return img ? { ...img, order: index } : null;
        })
        .filter((x): x is ImageClip => x !== null);
      return touch({ ...p, images });
    });
  },

  removeImage: (projectId, imageId) => {
    updateProjectField(set, projectId, (p) => {
      const images = p.images.filter((i) => i.id !== imageId).map((img, index) => ({ ...img, order: index }));
      return touch({ ...p, images });
    });
  },

  replaceImage: (projectId, imageId, mediaId) => {
    updateProjectField(set, projectId, (p) => ({
      ...touch(p),
      images: p.images.map((img) => (img.id === imageId ? { ...img, mediaId } : img)),
    }));
  },

  updateImageClip: (projectId, imageId, patch) => {
    updateProjectField(set, projectId, (p) => ({
      ...touch(p),
      images: p.images.map((img) => (img.id === imageId ? { ...img, ...patch } : img)),
    }));
  },

  applyDurationToAll: (projectId, duration) => {
    updateProjectField(set, projectId, (p) => ({
      ...touch(p),
      images: p.images.map((img) => ({ ...img, duration })),
    }));
  },

  updateVoiceover: (projectId, patch) => {
    updateProjectField(set, projectId, (p) => touch({ ...p, voiceover: { ...p.voiceover, ...patch } }));
  },
  updateCaptions: (projectId, patch) => {
    updateProjectField(set, projectId, (p) => touch({ ...p, captions: { ...p.captions, ...patch } }));
  },
  updateMusic: (projectId, patch) => {
    updateProjectField(set, projectId, (p) => touch({ ...p, music: { ...p.music, ...patch } }));
  },
  updateWatermark: (projectId, patch) => {
    updateProjectField(set, projectId, (p) => touch({ ...p, watermark: { ...p.watermark, ...patch } }));
  },
  updateExport: (projectId, patch) => {
    updateProjectField(set, projectId, (p) => touch({ ...p, export: { ...p.export, ...patch } }));
  },

  exportProjectJson: (id) => {
    const project = get().workspace.projects[id];
    return JSON.stringify(project, null, 2);
  },

  importProjectJson: (json) => {
    const parsed = JSON.parse(json) as ReelProject;
    const now = Date.now();
    const imported: ReelProject = { ...parsed, id: createId('reel'), createdAt: now, updatedAt: now };
    set((state) => {
      const workspace: Workspace = {
        ...state.workspace,
        projects: { ...state.workspace.projects, [imported.id]: imported },
        projectOrder: [...state.workspace.projectOrder, imported.id],
        activeProjectId: imported.id,
        updatedAt: now,
      };
      scheduleSave(workspace);
      return { workspace };
    });
    return imported.id;
  },

  enqueueJob: (projectId) => {
    const jobId = createId('job');
    set((state) => {
      const job: import('@/types').RenderJob = {
        id: jobId,
        projectId,
        status: 'queued',
        progress: 0,
        error: null,
        resultMediaId: null,
        resultFileName: null,
        resultSizeBytes: null,
        startedAt: null,
        completedAt: null,
      };
      const workspace: Workspace = {
        ...state.workspace,
        jobs: { ...state.workspace.jobs, [jobId]: job },
        jobOrder: [...state.workspace.jobOrder, jobId],
        updatedAt: Date.now(),
      };
      scheduleSave(workspace);
      return { workspace };
    });
    return jobId;
  },

  updateJob: (jobId, patch) => {
    set((state) => {
      const existing = state.workspace.jobs[jobId];
      if (!existing) return {};
      const workspace: Workspace = {
        ...state.workspace,
        jobs: { ...state.workspace.jobs, [jobId]: { ...existing, ...patch } },
        updatedAt: Date.now(),
      };
      scheduleSave(workspace);
      return { workspace };
    });
  },

  removeJob: (jobId) => {
    set((state) => {
      const jobs = { ...state.workspace.jobs };
      delete jobs[jobId];
      const jobOrder = state.workspace.jobOrder.filter((id) => id !== jobId);
      const workspace: Workspace = { ...state.workspace, jobs, jobOrder, updatedAt: Date.now() };
      scheduleSave(workspace);
      return { workspace };
    });
  },

  reorderJobs: (order) => {
    set((state) => {
      const workspace = { ...state.workspace, jobOrder: order, updatedAt: Date.now() };
      scheduleSave(workspace);
      return { workspace };
    });
  },

  clearAllData: async () => {
    releaseAllObjectUrls();
    await clearWorkspace();
    await pruneMedia(new Set());
    set({ workspace: createEmptyWorkspace() });
  },

  checkStorageHealth: async () => {
    const estimate = await estimateStorage();
    if (!estimate || estimate.quota === 0) return;
    const usageRatio = estimate.usage / estimate.quota;
    if (usageRatio > 0.85) {
      set({
        storageWarning:
          'Local storage is nearly full. Consider deleting unused projects or media before adding more Reels — the browser may otherwise refuse to save new data.',
      });
    } else {
      set({ storageWarning: null });
    }
  },
}));

function updateProjectField(
  set: (fn: (state: WorkspaceState) => Partial<WorkspaceState>) => void,
  projectId: string,
  updater: (project: ReelProject) => ReelProject
) {
  set((state) => {
    const project = state.workspace.projects[projectId];
    if (!project) return {};
    const updated = updater(project);
    const workspace: Workspace = {
      ...state.workspace,
      projects: { ...state.workspace.projects, [projectId]: updated },
      updatedAt: Date.now(),
    };
    scheduleSave(workspace);
    return { workspace };
  });
}

/** Removes any stored media not referenced by any project — call after deletes/replacements. */
export async function pruneUnusedMedia(workspace: Workspace): Promise<number> {
  const keep = new Set<string>();
  for (const project of Object.values(workspace.projects)) {
    for (const img of project.images) keep.add(img.mediaId);
    if (project.voiceover.generatedMediaId) keep.add(project.voiceover.generatedMediaId);
    if (project.music.mediaId) keep.add(project.music.mediaId);
    if (project.watermark.mediaId) keep.add(project.watermark.mediaId);
  }
  for (const setting of Object.values(workspace.shared)) {
    const m = setting as { mediaId?: string | null };
    if (m.mediaId) keep.add(m.mediaId);
  }
  return pruneMedia(keep);
}
